/**
 * Copy for the `/drivers` hub (MAR-76). Rationale and constraints live in
 * `.claude/rules/drivers.md`; the two rules a future editor is most likely to
 * break are: no superlatives ("Top-Rated", "Best") anywhere the copy reaches a
 * title or H1, and the CTA stays on the generic `shipments/new` chooser.
 */

export type DriversHubCopy = {
  /** Provider entity for filtered titles/H1s and the breadcrumb. */
  headingNoun: string
  /** Hub hero proof sentence for a formatted completed-shipment count. */
  shipmentProof: (count: string) => string
  pageTitle: string
  heroHeading: string
  heroSubheading: string
  heroDescription: string
  ctaLabel: string
  metaDescription: string
}

/**
 * `pet` is the default set. `generic` is used when the filters select non-pet
 * cargo (see `isPetTransportContext` in `lib/drivers/helpers/page-meta.ts`).
 * Both state the background check as fact: confirmed for every listed driver
 * by the product owner on 2026-09-08.
 */
export const DRIVERS_HUB_COPY: {
  pet: DriversHubCopy
  generic: DriversHubCopy
} = {
  pet: {
    shipmentProof: (count) =>
      `${count} pets safely transported nationwide through CitizenShipper since 2008.`,
    headingNoun: "Pet Transporters & Drivers",
    pageTitle: "Trusted Pet Transporters & Shipping Drivers | CitizenShipper",
    heroHeading:
      "Meet Some of the Pet Transporters & Drivers on CitizenShipper",
    heroSubheading:
      "Professional Pet Transportation and Shipping Services Across the US",
    heroDescription:
      "Browse a selection of experienced transporters on CitizenShipper, including drivers specializing in pet transportation. Every CitizenShipper driver undergoes a background check, and pet transporters meet our applicable USDA requirements. Compare profiles, reviews, completed shipments, and services before choosing the right transporter for your needs.",
    ctaLabel: "Get Pet Transport Quotes",
    metaDescription:
      "Browse a selection of trusted pet transporters and shipping drivers on CitizenShipper. Compare profiles, reviews, completed shipments, and services from background-checked drivers.",
  },
  generic: {
    shipmentProof: (count) =>
      `${count} shipments delivered nationwide through CitizenShipper since 2008.`,
    headingNoun: "Transporters & Drivers",
    pageTitle: "Trusted Transporters & Shipping Drivers | CitizenShipper",
    heroHeading: "Meet Some of the Transporters & Drivers on CitizenShipper",
    heroSubheading:
      "Professional Transportation and Shipping Services Across the US",
    heroDescription:
      "Browse a selection of experienced transporters on CitizenShipper. Every CitizenShipper driver undergoes a background check. Compare profiles, reviews, completed shipments, and services before choosing the right transporter for your needs.",
    ctaLabel: "Get Transportation Quotes",
    metaDescription:
      "Browse a selection of trusted transporters and shipping drivers on CitizenShipper. Compare profiles, reviews, completed shipments, and services from background-checked drivers.",
  },
}

/** Sidebar note that replaced the Background Check checkbox. */
export const PLATFORM_BACKGROUND_CHECK_NOTE =
  "Every driver on CitizenShipper passes a background check. It is a platform requirement for all transporters."

/** Appended to every filtered hero body. */
export const DRIVERS_SELECTION_NOTE =
  "This directory shows a selection of the CitizenShipper network, and all CitizenShipper drivers undergo a background check."

/** Generic quote chooser, not the pet flow: the roster is not pet-only. */
export const DRIVERS_CTA_PATH = "shipments/new"

/** The stock hero image is a pet illustration, whatever the filter state. */
export const DRIVERS_STOCK_HERO_ALT = "Pet transport illustration"

export function driverHeroPhotoAlt(displayName: string): string {
  return `${displayName}, a transporter on CitizenShipper`
}
